import React, { useLayoutEffect, useRef, useState } from 'react';

/**
 * Wraps children with a fade/slide-in animation triggered once
 * the element scrolls into the viewport.
 * direction: 'up' | 'left' | 'right'
 *
 * Visibility is resolved in useLayoutEffect (before paint) and checked
 * synchronously via getBoundingClientRect for anything already on screen
 * at mount, so above-the-fold content never sits invisible waiting on an
 * observer callback.
 */
// Caps stagger delay so long lists (e.g. 8-9 items) can't push the last
// card's animation start far enough out that fast scrolling outruns it.
const MAX_STAGGER_DELAY = 240;

function Reveal({ children, direction = 'up', delay = 0, className = '' }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const cappedDelay = Math.min(delay, MAX_STAGGER_DELAY);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;

    const rect = node.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0, rootMargin: '350px 0px 350px 0px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`reveal reveal-${direction} ${visible ? 'reveal-visible' : ''} ${className}`}
      style={{ transitionDelay: `${cappedDelay}ms` }}
    >
      {children}
    </div>
  );
}

export default Reveal;
